import {
  ColumnDef,
  ExpandedState,
  functionalUpdate,
  getExpandedRowModel,
  GroupingState,
  InitialTableState,
  Row,
  RowSelectionState,
  TableOptions,
  Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { createAggregatedListing } from 'src/components/Listings/listingUtils';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useInputPriceFocusContext } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import {
  MultiSelectScope,
  NO_GROUP_ID,
  SelectionMode,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { usePosLiteAccount } from 'src/hooks/usePosLiteAccount';
import { useSortListingTableSeatingColumn } from 'src/hooks/useSortListingTableSeatingColumn';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { NoData, Table } from 'src/tables/Table';
import {
  defaultListingFlattenedColumnsConfig,
  filterColumnsByFeatures,
  getListingColumnConfigById,
} from 'src/utils/columns/columnUtils';
import {
  ListingTableColumnId,
  ListingTableFlattenedColumnId,
} from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { filterCustomColumnsForListing } from 'src/utils/columns/inventory/inventoryCustomColumnUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasAllPermissions } from 'src/utils/userPermissionUtils';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Feature,
  GroupType,
  Listing,
  ListingGroup,
  UserSetting,
} from 'src/WebApiController';

import { usePaginationSettings } from '../Table/hooks/usePaginationSettings';
import { RowWrapperV2 } from '../TableV2/types';
import {
  customColumnDef,
  getListingTableColumnConfigs,
  tagColumnDef,
} from './configs/ListingTableColumnsConfig';
import { LISTING_TABLE_FLATTENED_COLUMNS_CONFIG } from './configs/ListingTableFlattenedColumnsConfig';
import { useTableRowWrapper } from './ListingTable.hooks';
import { ListingWithEvent } from './ListingTable.types';
import { ListingTableActionContextWrapper } from './ListingTableActionContext';

export type ListingTableFlattenedProps = {
  listings?: ListingWithEvent[];
  listingsSummary?: ListingWithEvent | null;
  listingCount: number;
  ungroupedListingCount: number;
  listingGroupCount: number;
  failedToRetrieveData: boolean;
  onMount?: () => void;
  onUnmount?: (state: object) => void;
  initState?: InitialTableState;
  selectionMode?: SelectionMode | null;
  selectedIds?: number[];
  onItemSelectionsChanged?: (newSelectedIds: number[]) => void;
  disablePagination?: boolean;
  useVirtuoso?: boolean;
  columnVisibilityOverride?: (
    | ListingTableColumnId
    | ListingTableFlattenedColumnId
  )[];
  formatOptionOverrides?: FormatOptionEntries;
  withOuterPadding?: boolean;
  disableSelection?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllOnHeader?: boolean;
  enableColumnFilters?: boolean;
  ignoreMaxCount?: boolean;
  groupingStateReadonly?: GroupingState;
  useDataGrouping?: boolean;
};

const PAGE_SIZE = 100;

const getRowId = (originalRow: ListingWithEvent | null, index: number) =>
  (originalRow?.listing?.id ?? index).toString();

export const ListingTableFlattened = ({
  listings,
  listingsSummary,
  listingCount,
  ungroupedListingCount,
  listingGroupCount,
  failedToRetrieveData,
  selectedIds,
  selectionMode: defaultSelectionMode,
  onItemSelectionsChanged,
  disablePagination,
  onMount,
  onUnmount,
  initState,
  useVirtuoso,
  columnVisibilityOverride,
  formatOptionOverrides,
  withOuterPadding = true,
  disableSelection,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
  enableColumnFilters,
  ignoreMaxCount,
  groupingStateReadonly,
  useDataGrouping = false,
}: ListingTableFlattenedProps) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Listing
  );
  const isPosLite = usePosLiteAccount();

  const { appContext, loginContext } = useAppContext();

  const { sorting, setSorting } = useActiveSortTableColumnContext();
  const {
    selectionMode: selectionModeOverride,
    setGroupItems,
    getSelection,
    getGroupToggleState,
    isAllSelected,
    updateGroupFilteredIds,
  } = useMultiSelectionContext();

  const [grouping, setGrouping] = useState<GroupingState>(
    groupingStateReadonly ?? initState?.grouping ?? []
  );

  useEffect(() => {
    if (!isEqual(grouping, groupingStateReadonly ?? [])) {
      setGrouping(groupingStateReadonly ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupingStateReadonly]);

  useDataGrouping = useDataGrouping || groupingStateReadonly != null;

  const selectionMode = selectionModeOverride ?? defaultSelectionMode;

  const listingSelection = getSelection(NO_GROUP_ID);
  const hasSortListingsInGroupFeature = useUserHasFeature(
    Feature.SortListingsInGroup
  );
  const hasSurfaceSurfaceFirstListingInGroupFeature = useUserHasFeature(
    Feature.SurfaceFirstListingInGroup
  );

  const hideHiddenByFilter = useUserHasFeature(Feature.HideHiddenByFilter);
  const hasMultiValueTagsInColumns = useUserHasFeature(
    Feature.MultiValueTagsInColumns
  );

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  const [expandedState, setExpandedState] = useState<ExpandedState>(
    initState?.expanded ?? {}
  );

  // This is to pre-expand all the groups on first-load
  useEffect(() => {
    if (
      Object.keys(expandedState).length === 0 &&
      listings?.some((l) => l.isLtGrp)
    ) {
      setExpandedState(
        listings?.reduce(
          (r, l) => {
            if (l.isLtGrp) {
              r[l.listing!.id.toString()] = true;
            }

            return r;
          },
          {} as Record<string, boolean>
        ) ?? {}
      );
    }
  }, [expandedState, listings]);

  const { inputPriceFocusContext } = useInputPriceFocusContext();
  const { isItemsLoading } = useCatalogDataContext();
  const currentPage = useMemo(() => {
    return inputPriceFocusContext &&
      inputPriceFocusContext.rowIndex < listingCount
      ? inputPriceFocusContext.currentPageIndex
      : -1;
  }, [inputPriceFocusContext, listingCount]);

  const { data, hasNoDataLoad } = useMemo(() => {
    const data =
      listings == null || isItemsLoading
        ? (generateShimmeringRows(
            ungroupedListingCount,
            listingGroupCount
          ) as (ListingWithEvent | null)[])
        : listings;

    const hasNoDataLoad =
      !ignoreMaxCount && listingCount > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS;
    return { data, hasNoDataLoad };
  }, [
    listings,
    isItemsLoading,
    ungroupedListingCount,
    listingGroupCount,
    ignoreMaxCount,
    listingCount,
  ]);

  const { pagination, setPagination } = usePaginationSettings(
    data.length,
    currentPage,
    PAGE_SIZE,
    disablePagination,
    initState
  );

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ pagination, sorting, grouping });
  }, [pagination, sorting, onUnmount, grouping]);

  const rowSelection = useMemo(() => {
    return data
      ?.flatMap((d) => [
        d?.listing,
        ...((d?.listing as ListingGroup)?.groupItems ?? []),
        ...((d?.listing as ListingGroup)?.groupItems ?? []).flatMap(
          (lg) => (lg as ListingGroup).groupItems ?? []
        ),
      ])
      ?.reduce<Record<string, boolean>>((result, listing) => {
        if (listing?.id) {
          const hasClientSideFilter = !!listingSelection?.items.itemIds.length;

          const isAllSelectedWithoutClientSideFilter =
            isAllSelected && !hasClientSideFilter;

          const isGroupSelectedWithoutClientSideFilter =
            getGroupToggleState(NO_GROUP_ID).isGroupSelected &&
            !hasClientSideFilter;

          result[listing.id] = isAllSelectedWithoutClientSideFilter
            ? true
            : ((isGroupSelectedWithoutClientSideFilter ||
                (listingSelection?.items.itemIds ?? selectedIds)?.includes(
                  String(listing?.id)
                )) ??
              false);
        }
        return result;
      }, {});
  }, [
    data,
    getGroupToggleState,
    isAllSelected,
    listingSelection?.items.itemIds,
    selectedIds,
  ]);

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      if (disableSelection) return;

      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      const newSelectedIds = Object.keys(newSelections).filter(
        (r) => newSelections[r] === true
      );
      if (selectionMode?.mode === 'singleItem') {
        newSelectedIds.forEach((id) => {
          if (rowSelection[id] && newSelections[id]) {
            // if both of these are the same value and is true, we need to set it to false
            newSelections[id] = false;
          }
        });
      }
      setGroupItems(NO_GROUP_ID, newSelections);
      onItemSelectionsChanged?.(
        newSelectedIds
          .filter((id) => newSelections[id])
          .map((id) => parseInt(id))
      );
    },
    [
      disableSelection,
      rowSelection,
      selectionMode?.mode,
      setGroupItems,
      onItemSelectionsChanged,
    ]
  );

  const {
    value:
      storedInventoryFlattenedColumnOrderSetting = defaultListingFlattenedColumnsConfig,
  } = useServerUserSetting<ListingTableColumnId[]>({
    id: UserSetting.InventoryFlattenedColumnOrder,
  });
  const { value: storedInventoryColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: UserSetting.InventoryFlattenedColumnsEnabled,
  });

  const { value: customListingColumns = [] } = useServerUserSetting<
    CustomListingColumn[]
  >({
    id: UserSetting.InventoryCustomColumns,
  });
  const { tagsMetadata, tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Listing,
    true
  );

  const customListingColumnsFiltered = filterCustomColumnsForListing(
    customListingColumns,
    tagsMetadataNumeric
  );

  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      listingSelection.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      listingSelection.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  const allColumnConfigs = useMemo(
    () =>
      getListingTableColumnConfigs(
        hasSortListingsInGroupFeature,
        false,
        hasSurfaceSurfaceFirstListingInGroupFeature,
        useDataGrouping
      ),
    [
      hasSortListingsInGroupFeature,
      hasSurfaceSurfaceFirstListingInGroupFeature,
      useDataGrouping,
    ]
  );

  const seatingColumnSorting = useSortListingTableSeatingColumn(
    SectionType.ListingsFlattened,
    useDataGrouping
  );

  const displayedColumnsConfig = useMemo(() => {
    const inventoryColumnOrder = columnVisibilityOverride
      ? columnVisibilityOverride
      : storedInventoryFlattenedColumnOrderSetting.filter(
          // If storedInventoryColumnsEnabledSetting has never been set, default to true
          (c) => storedInventoryColumnsEnabledSetting?.includes(c) ?? true
        );

    const listingTableColumnConfigs = Object.values(allColumnConfigs);
    const listingTableFlattenedColumnConfigs = Object.values(
      LISTING_TABLE_FLATTENED_COLUMNS_CONFIG
    );

    const columns = new Set(
      columnVisibilityOverride
        ? columnVisibilityOverride
            .map(
              (cid) =>
                listingTableColumnConfigs.find((c) => c.id === cid) ??
                listingTableFlattenedColumnConfigs.find((c) => c.id === cid)!
            )
            .map((item) => item.id!)
        : [
            // Add unconfigurable columns first
            ...listingTableFlattenedColumnConfigs
              .filter((column) => {
                const columnDef = getListingColumnConfigById(
                  column.id as ListingTableFlattenedColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );

                const hasPermissionForColumn =
                  columnDef.requiredPermissions.length === 0 ||
                  hasAllPermissions(
                    loginContext?.user,
                    columnDef.requiredPermissions
                  );

                return (
                  !columnDef.isConfigurable &&
                  hasFeatureForColumn &&
                  hasPermissionForColumn
                );
              })
              .map((item) => item.id!),
            ...listingTableColumnConfigs
              .filter((column) => {
                const columnDef = getListingColumnConfigById(
                  column.id as ListingTableColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );

                const isConfigurable = columnDef.isConfigurable;
                const hasPermissionForColumn =
                  columnDef.requiredPermissions.length === 0 ||
                  hasAllPermissions(
                    loginContext?.user,
                    columnDef.requiredPermissions
                  );
                return (
                  !isConfigurable &&
                  hasFeatureForColumn &&
                  hasPermissionForColumn
                );
              })
              .map((item) => item.id!),
            // Add user defined columns next
            ...filterColumnsByFeatures(
              inventoryColumnOrder,
              SectionType.ListingsFlattened,
              customListingColumnsFiltered,
              loginContext?.user,
              appContext?.features,
              tagsMetadata ?? []
            ),
          ]
    );

    if (isPosLite) {
      columns.delete(ListingTableColumnId.Actions);
    }

    if (!columnVisibilityOverride) {
      if (showCheckbox) {
        if (!alwaysShowCheckbox) {
          columns.delete(ListingTableColumnId.Actions);
        }
      } else {
        columns.delete(ListingTableColumnId.Checkbox);
      }
    }

    return Array.from(columns).reduce<ColumnDef<ListingWithEvent | null>[]>(
      (acc, columnId) => {
        if (
          !columnVisibilityOverride &&
          tagsMetadata?.find((t) => t.key === columnId)
        ) {
          acc.push(
            tagColumnDef(
              columnId,
              undefined,
              false,
              false,
              false,
              hasMultiValueTagsInColumns
            )
          );
        } else if (
          !columnVisibilityOverride &&
          customListingColumnsFiltered.find((c) => c.id === columnId)
        ) {
          acc.push(
            customColumnDef(
              columnId,
              customListingColumnsFiltered.find((c) => c.id === columnId)
                ?.formula
            )
          );
        } else {
          const columnConfig1 = listingTableColumnConfigs.find(
            (column) => column.id === columnId
          );
          if (columnConfig1) {
            acc.push(columnConfig1 as ColumnDef<ListingWithEvent | null>);
          }
          const columnConfig2 = listingTableFlattenedColumnConfigs.find(
            (column) => column.id === columnId
          );
          if (columnConfig2) {
            acc.push(columnConfig2 as ColumnDef<ListingWithEvent | null>);
          }
        }

        return acc;
      },
      []
    );
  }, [
    columnVisibilityOverride,
    storedInventoryFlattenedColumnOrderSetting,
    allColumnConfigs,
    customListingColumnsFiltered,
    loginContext?.user,
    appContext?.features,
    tagsMetadata,
    hasMultiValueTagsInColumns,
    storedInventoryColumnsEnabledSetting,
    showCheckbox,
    alwaysShowCheckbox,
    isPosLite,
  ]);

  const [visibleRowIds, setVisibleRowIds] = useState<number[]>();

  const listingsSummaryFiltered = useMemo(() => {
    if (!listingsSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      return createAggregatedListing(
        data
          ?.filter((l) => !l?.isCompListing)
          ?.flatMap((l) => flattenListingGroup(l?.listing))
          .filter((l) => l?.isFull)
          .map((l) => ({
            listing: l as Listing,
            event: data[0]!.event,
          }))
          ?.filter(
            (d, i) =>
              d?.listing && visibleRowIds.includes(Number(getRowId(d, i)))
          )
          .map((d) => d!)
      );
    }
    return listingsSummary;
  }, [visibleRowIds, data, listingsSummary, hasTableTotalsFeature]);

  const options: Partial<TableOptions<ListingWithEvent | null>> = useMemo(
    () => ({
      data,
      columns: displayedColumnsConfig,
      state: {
        grouping,
        pagination,
        sorting,
        rowSelection,
        expanded: useDataGrouping ? true : expandedState,
        columnVisibility: {
          [ListingTableColumnId.TicketClassColor]: false,
        },
        columnPinning: {
          right: [ListingTableColumnId.Actions],
        },
      },
      meta: {
        sectionType: SectionType.ListingsFlattened,
        formatOptionOverrides,
        showSelectAllCheckbox: showSelectAllOnHeader,
        summaryData: listingsSummaryFiltered,
      },
      getRowId,
      onPaginationChange: setPagination,
      onSortingChange: (sortingUpdaterFn) => {
        const newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
        setSorting(newSortVal);
      },
      onExpandedChange: setExpandedState,
      getSubRows: (originalRow) => {
        if (!originalRow?.listing || !originalRow.listing.isLtGrp) {
          return [];
        }

        const listingGroup = originalRow.listing as ListingGroup;
        const result =
          [...(listingGroup.groupItems ?? [])]
            .filter(
              (l) =>
                !hideHiddenByFilter ||
                !l.isLtGrp ||
                (l as ListingGroup).groupType !== GroupType.Invisible
            )
            .toSorted((a, b) => {
              if (a.availQty <= 0 && b.availQty >= 0) {
                return 1;
              } else if (a.availQty >= 0 && b.availQty <= 0) {
                return -1;
              }
              return (a.ltGrpPrior ?? -1) - (b.ltGrpPrior ?? -1);
            })
            ?.map(
              (l, i) =>
                ({
                  ...originalRow,
                  rowIndex: i,
                  listing: l,
                }) as ListingWithEvent
            ) ?? [];

        return result;
      },
      getRowCanExpand: (originalRow) => {
        return Boolean(
          originalRow.original?.listing?.isLtGrp &&
          (originalRow.original?.listing as ListingGroup).groupType !==
            GroupType.Invisible
        );
      },
      onGroupingChange: groupingStateReadonly ? undefined : setGrouping,
      getExpandedRowModel: (table) => getExpandedRowModel()(table),
      paginateExpandedRows: false,
      enableRowSelection: showCheckbox,
      enableMultiRowSelection: showCheckbox,
      onRowSelectionChange,
      enableSubRowSelection: showCheckbox,
      sortingFns: { ...seatingColumnSorting },
      enableGrouping: useDataGrouping,
    }),
    [
      data,
      displayedColumnsConfig,
      grouping,
      hideHiddenByFilter,
      pagination,
      sorting,
      rowSelection,
      useDataGrouping,
      expandedState,
      formatOptionOverrides,
      showSelectAllOnHeader,
      listingsSummaryFiltered,
      setPagination,
      groupingStateReadonly,
      showCheckbox,
      onRowSelectionChange,
      seatingColumnSorting,
      setSorting,
    ]
  );

  const { onRowWrapper, onRowWrapperV2 } = useTableRowWrapper(
    undefined,
    pagination,
    displayedColumnsConfig
  );

  const onFilteredRowsChange = useCallback(
    (filteredRows: Row<ListingWithEvent | null>[] | undefined) => {
      const filteredItemIds = filteredRows
        ?.filter((r) => !r.original?.isLtGrp && !r.original?.isCompListing)
        .map((r) => r.original?.listing?.id)
        .filter((lid) => lid != null) as number[] | undefined;
      updateGroupFilteredIds(NO_GROUP_ID, filteredItemIds);
      setVisibleRowIds(filteredItemIds);
    },
    [updateGroupFilteredIds]
  );

  const eventIds = useMemo(() => {
    return Array.from(
      new Set((listings ?? []).map((l) => l.event.virtId))
    ).toSorted();
  }, [listings]);

  return failedToRetrieveData ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : hasNoDataLoad ? (
    <NoData>
      <WarningMessage
        message={
          <FormatContent
            id={FormatContentId.ApplyFilterToReduceCountsToSeeData}
            params={[MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS.toString()]}
          />
        }
      />
    </NoData>
  ) : data?.length > 0 ? (
    <ListingTableActionContextWrapper viagogoVirtualIds={eventIds}>
      <ColumnResizingContextProvider
        userSettingId={UserSetting.InventoryColumnWidths}
      >
        <Table
          withOuterPadding={withOuterPadding}
          options={options}
          rowWrapper={onRowWrapper}
          rowWrapperV2={
            onRowWrapperV2 as RowWrapperV2<ListingWithEvent | null> | undefined
          }
          tableLayout="fixed"
          tableStyle={
            disablePagination
              ? {
                  maxHeight: '60vh',
                }
              : undefined
          }
          useStickyHeader={disablePagination}
          tableCellStyle={{ overflow: 'hidden' }}
          useVirtuoso={useVirtuoso}
          enableColumnFilters={enableColumnFilters}
          onFilteredRowsChange={
            enableColumnFilters ? onFilteredRowsChange : undefined
          }
          disableFooter={!listingsSummaryFiltered}
          useStickyFooter
          useDataGrouping={useDataGrouping}
        />
      </ColumnResizingContextProvider>
    </ListingTableActionContextWrapper>
  ) : null;
};
